import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  useGetCompaniesQuery, useCreateCompanyMutation, useDeleteCompanyMutation,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, FilterBar, SearchBox, DataTable, StatusBadge,
  ActionButtons, ActionButton, SlidePanel, ConfirmDialog, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const STATUSES = ["trial", "active", "suspended"];

const empty = {
  name: "", business_type: "", status: "trial", timezone: "America/Toronto",
  country: "CA", primary_phone: "", primary_email: "", website: "",
};

const Companies: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetCompaniesQuery();
  const [createCompany, { isLoading: saving }] = useCreateCompanyMutation();
  const [deleteCompany] = useDeleteCompanyMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(empty);
  const [search, setSearch] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const rows = data?.data ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c: any) =>
      [c.name, c.companyName, c.slug, c.primary_email, c.business_type]
        .filter(Boolean).some((v: string) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  const openCreate = () => { setForm(empty); setOpen(true); };
  const save = async () => { await createCompany(form).unwrap(); setOpen(false); };
  const set = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });

  const columns: DataTableColumn<any>[] = [
    {
      id: "name", label: "Company",
      render: (c) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            src={c.logo}
            sx={{ width: 36, height: 36, bgcolor: "rgba(11,30,45,0.08)", color: NAVY, fontWeight: 700, fontSize: 14 }}
          >
            {(c.name ?? c.companyName ?? "?").charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography fontSize={14} fontWeight={700} color={NAVY}>{c.name ?? c.companyName}</Typography>
            <Typography fontSize={12} color="text.secondary">{c.slug}</Typography>
          </Box>
        </Stack>
      ),
    },
    { id: "business_type", label: "Type", render: (c) => c.business_type || "—" },
    { id: "status", label: "Status", render: (c) => <StatusBadge status={c.status} /> },
    { id: "country", label: "Country", render: (c) => c.country || "—" },
    { id: "primary_email", label: "Email", render: (c) => c.primary_email || "—" },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<BusinessRoundedIcon />}
          title="Company Management"
          subtitle="Tenants on the INTALK platform"
          actions={
            <ActionButton startIcon={<AddRoundedIcon />} onClick={openCreate}>
              Add Company
            </ActionButton>
          }
        />
      }
    >
      <FilterBar right={<Typography fontSize={13} color="text.secondary">{filtered.length} companies</Typography>}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search companies…" />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        onRowClick={(c) => navigate(`/companies/${c.id}`)}
        actions={(c) => (
          <ActionButtons
            onView={() => navigate(`/companies/${c.id}`)}
            onDelete={() => setConfirmId(c.id)}
          />
        )}
        empty={{
          icon: <BusinessRoundedIcon />,
          title: "No companies yet",
          message: "Create your first tenant to start managing numbers, messaging and billing.",
          action: <ActionButton startIcon={<AddRoundedIcon />} onClick={openCreate}>Add Company</ActionButton>,
        }}
      />

      <SlidePanel
        open={open}
        onClose={() => setOpen(false)}
        title="Add Company"
        subtitle="Create a new tenant"
        icon={<BusinessRoundedIcon />}
        footer={
          <>
            <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>Cancel</Button>
            <ActionButton onClick={save} disabled={!form.name || saving}>{saving ? "Saving…" : "Save Company"}</ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          <TextField label="Name" value={form.name} onChange={set("name")} fullWidth required />
          <TextField label="Business Type" value={form.business_type} onChange={set("business_type")} fullWidth />
          <TextField select label="Status" value={form.status} onChange={set("status")} fullWidth>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Timezone" value={form.timezone} onChange={set("timezone")} fullWidth />
            <TextField label="Country" value={form.country} onChange={set("country")} sx={{ width: 120 }} />
          </Stack>
          <TextField label="Primary Phone" value={form.primary_phone} onChange={set("primary_phone")} fullWidth />
          <TextField label="Primary Email" value={form.primary_email} onChange={set("primary_email")} fullWidth />
          <TextField label="Website" value={form.website} onChange={set("website")} fullWidth />
        </Stack>
      </SlidePanel>

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete company?"
        message="This permanently removes the tenant and its data. This cannot be undone."
        confirmText="Delete"
        danger
        onClose={() => setConfirmId(null)}
        onConfirm={async () => { if (confirmId !== null) await deleteCompany(confirmId); setConfirmId(null); }}
      />
    </AppPage>
  );
};

export default Companies;
